import React from 'react';
import { vars } from '@croquiscom-pvt/zds/tokens';
import { css } from '@emotion/react';
import styled from '@emotion/styled';

import { useAppConfig } from '@common/app-config';
import { Typography } from '@common/styles';
import { PDPCustomAttributeKeys } from '@common/test-automation';
import { useZackeyPdpEntry, ZackeyPdpEntryButton } from '@widgets/traffic-growth';
import { useCatalogProductIdParams } from '@domains/pdp-common';

import { KakaoPurchaseButton } from '../components/KakaoPurchaseButton';
import { ScrollButtons } from '../components/ScrollButtons';
import { useBottomFloatingContainerService } from '../services/useBottomFloatingContainerService';
import { useScrollService } from '../services/useScrollService';
import { DEFAULT_SAFE_AREA_INSET_BOTTOM, notchiPhoneMediaQuery } from '../utils/notchiPhoneMediaQuery';
import { InAppSubButtonContainer } from './InAppSubButtonContainer';
import { SubButtonContainer } from './SubButtonContainer';

interface BottomFloatingContainerProps {
  onClick: () => void;
}
export const BottomFloatingContainer: React.FC<React.PropsWithChildren<BottomFloatingContainerProps>> = ({
  children,
  onClick,
}) => {
  const { isZigzagApp } = useAppConfig();
  const { handleScroll } = useScrollService();
  const { catalog_product_id } = useCatalogProductIdParams();
  const { shouldShow, sessionId } = useZackeyPdpEntry(catalog_product_id);
  const shouldShowZackeyPdpEntry = isZigzagApp && shouldShow;

  const {
    product_id,
    shop_id,
    is_preview,
    isPurchasable,
    buyButton,
    safe_area_inset_bottom,
    showPurchaseWithKakao,
    onClickPurchaseButton,
    onClickPurchaseKakaoButton,
    renderButtonContents,
  } = useBottomFloatingContainerService({ onClick });

  if (is_preview) {
    return (
      <Container isPreview={true} safe_area_inset_bottom={safe_area_inset_bottom} isApp={isZigzagApp}>
        <Wrap>
          <PurchaseButton
            type='button'
            textColor={buyButton.text.enabled}
            buttonColor={buyButton.background.enabled}
            onClick={onClickPurchaseButton}
            data-allow-preview
          >
            구매 옵션 보기
          </PurchaseButton>
        </Wrap>
      </Container>
    );
  }

  if (showPurchaseWithKakao) {
    return (
      <div>
        {children}
        <Container safe_area_inset_bottom={safe_area_inset_bottom} isApp={isZigzagApp}>
          <Wrap>
            <SubButtonContainer product_id={product_id} shop_id={shop_id} />
            <KakaoPurchaseButton
              onClick={onClickPurchaseKakaoButton}
              data-custom-ta-key={PDPCustomAttributeKeys.PDP_PURCHASE_BUTTON_7}
            />
          </Wrap>
        </Container>
        <ScrollButtons handleClick={handleScroll} />
      </div>
    );
  }

  return (
    <div>
      {children}
      <Container safe_area_inset_bottom={safe_area_inset_bottom} isApp={isZigzagApp}>
        <Wrap>
          {isZigzagApp ? (
            <InAppSubButtonContainer product_id={product_id} />
          ) : (
            <SubButtonContainer product_id={product_id} shop_id={shop_id} />
          )}
          <PurchaseButton
            type='button'
            onClick={onClickPurchaseButton}
            textColor={buyButton.text.enabled}
            buttonColor={buyButton.background.enabled}
            disabledButtonColor={buyButton.background.disabled}
            disabled={!isPurchasable}
            className={Typography.BODY_17_BOLD}
            data-custom-ta-key={PDPCustomAttributeKeys.PDP_PURCHASE_BUTTON_7}
            css={shouldShowZackeyPdpEntry && purchaseButtonWithZackeyMarginCss}
          >
            {renderButtonContents()}
          </PurchaseButton>
          {shouldShowZackeyPdpEntry && (
            <ZackeyPdpEntryButton catalogProductId={catalog_product_id} sessionId={sessionId} />
          )}
        </Wrap>
      </Container>
      <ScrollButtons handleClick={handleScroll} />
    </div>
  );
};

const Container = styled.div<{ isPreview?: boolean; safe_area_inset_bottom: string | null; isApp: boolean }>`
  -webkit-tap-highlight-color: transparent;
  background: ${vars.v3Color.staticWhite};
  padding-bottom: 9px;
  position: relative;
  z-index: 999;

  ${({ safe_area_inset_bottom }) =>
    safe_area_inset_bottom &&
    css`
      padding-bottom: calc(${safe_area_inset_bottom} + 9px);
    `}

  ${({ isPreview }) =>
    isPreview &&
    css`
      button {
        font-size: 16px;
        font-weight: bold;
        color: ${vars.v3Color.staticWhite};
      }
    `}
`;

const Wrap = styled.div`
  padding: 8px 12px;
  display: flex;
  align-items: center;
  width: 100%;
  border-top: 1px solid ${vars.v3Color.gray100};
`;

const PurchaseButton = styled.button<{ textColor: string; buttonColor: string; disabledButtonColor?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 46px;
  border-radius: 24px;
  background: ${({ buttonColor }) => buttonColor} !important;
  cursor: pointer;
  color: ${({ textColor }) => textColor} !important;

  &:disabled {
    background: ${({ disabledButtonColor }) => disabledButtonColor} !important;
  }
`;

const purchaseButtonWithZackeyMarginCss = css`
  margin-right: 4px;
`;
